import React, { Component } from "react";
import {
  containerFluid as lgContainerFluid,
  sidebar,
  sidebarOpened,
  tableWrapper,
  tableWrapperShrinked
} from "./App.css";

import { containerFluid, row, col } from "bootstrap/dist/css/bootstrap.css";

import LogoVersionRequest from "./models/LogoVersionRequest";
// eslint-disable-next-line no-unused-vars
import LogoVersion from "./models/LogoVersion";
import BrandRequest from "./models/BrandRequest";
import Brand from "./models/Brand";

import Table from "./Table.js";
import Sidebar from "./Sidebar";
import Toolbar from "./Toolbar";
import DataService from "./DataService";
import {
  BRAND_REQUEST_STATUSES,
  LOGO_VERSION_REQUEST_STATUSES,
  BRAND_REQUEST_GROUP_STATUSES,
  BRAND_REQUEST_BRAND_STATUSES
} from "./enums";
import UploadBatchModal from "./UploadBatchModal";
import BrandSearchModal from "./BrandSearchModal";
import { ReferenceImagesModal } from "./ReferenceImagesModal";
import Utils from "./Utils";
import {toast} from "react-toastify";

/**
 * @typedef {object} Props
 * @prop {{params: {brandRequestGroupId: string}}} match
 * @prop {boolean} isAdmin
 *
 *
 * @extends {Component<Props>}
 */
class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSideBarOpen: false,
      /** @type {BrandRequest[]} */
      brandRequests: [],
      selectedBrandRequest: new BrandRequest(),
      /** @type {BrandRequestGroup} */
      brandRequestGroup: { status: BRAND_REQUEST_GROUP_STATUSES.FINISHED },
      selectedBrandIndexes: {},
      hasVisualDuplicates: false
    };

    this.onTableRowClick = this.onTableRowClick.bind(this);
    this.onSidebarConfirmClick = this.onSidebarConfirmClick.bind(this);
    this.onSidebarIgnoreClick = this.onSidebarIgnoreClick.bind(this);
    this.onLogoSelected = this.onLogoSelected.bind(this);
    this.onBatchUploadClick = this.onBatchUploadClick.bind(this);
    this.onToolbarDeleteBrandRequestGroup = this.onToolbarDeleteBrandRequestGroup.bind(
      this
    );
    this.onToolbarUpdateBrandRequestGroupName = this.onToolbarUpdateBrandRequestGroupName.bind(
      this
    );
    this.onSidebarBrandRequestChange = this.onSidebarBrandRequestChange.bind(
      this
    );
    this.onSidebarBlur = this.onSidebarBlur.bind(this);
    this.onBrandSelect = this.onBrandSelect.bind(this);
    this.onTableSimilarBrandSelect = this.onTableSimilarBrandSelect.bind(this);
    this.onSidebarAddNewMeta = this.onSidebarAddNewMeta.bind(this);
    this.onSidebarDeleteMeta = this.onSidebarDeleteMeta.bind(this);
  }

  async componentDidMount() {
    const { brandRequestGroupId } = this.props.match.params;
    const brandRequests = await DataService.getBrandRequests(
      brandRequestGroupId
    );
    const brandRequestGroup = await DataService.getBrandRequestGroup(
      brandRequestGroupId
    );

    // Pre-select Similar Brands that have LogoVersions checked already
    const selectedBrandIndexes = App.calculateSelectedBrandIndexes(
      brandRequests
    );

    this.setState({
      brandRequests,
      brandRequestGroup,
      selectedBrandIndexes,
      hasVisualDuplicates: brandRequests.some(br =>
        br.brandRequestBrands.some(
          brb => brb.comparisonMethod === "VisualSimilarity"
        )
      )
    });
  }

  /**
   *
   * @param {BrandRequest[]} brandRequests
   */
  static calculateSelectedBrandIndexes(brandRequests) {
    const selectedBrandIndexes = brandRequests.reduce(
      (arr, brandRequest, index) => {
        const checkedLogoVersionRequests = brandRequest.logoVersionRequests.filter(
          logoVersionRequest =>
            logoVersionRequest.status === LOGO_VERSION_REQUEST_STATUSES.CHECKED
        );
        const checkedBrandIds = checkedLogoVersionRequests.map(
          logoVersionRequest => logoVersionRequest.brandId
        );

        let selectedBrandIndex =
          brandRequest.brandRequestBrands.findIndex(
            brb => brb.status === BRAND_REQUEST_BRAND_STATUSES.SELECTED
          ) + 1 || -1;

        // If the new method didn't work, try old method
        if (selectedBrandIndex === -1) {
          // If all the checked logoVersions lack brandId - it's a New "+" brand
          selectedBrandIndex =
            checkedLogoVersionRequests.length &&
            checkedLogoVersionRequests.every(lvr => lvr.brandId === null)
              ? 0
              : // Otherwise find it in Similar Brands list
                brandRequest.brands.findIndex(
                  brand => checkedBrandIds.indexOf(brand.id) !== -1
                );
        }

        // If no LogoVersions are checked (-1) default to brand #0
        if (selectedBrandIndex === -1) {
          if (brandRequest.status === BRAND_REQUEST_STATUSES.NEW) {
            arr[index] = 0;
          } else {
            const onlyCreateNew =
              brandRequest.brands.length === 1 && !brandRequest.brands[0].id;
            // undefined means select nothing, should happen only on the first load
            arr[index] = onlyCreateNew ? undefined : 1;
          }
        } else {
          arr[index] = selectedBrandIndex;
        }

        return arr;
      },
      {}
    );

    return selectedBrandIndexes;
  }

  onTableRowClick(brandRequest) {
    this.setState({
      isSideBarOpen: true,
      selectedBrandRequest: brandRequest
    });
  }

  /**
   *
   * @param {LogoVersionRequest | LogoVersion} logoVersionBase - could be either existing LogoVersion or LogoVersion uploaded by user in form of a LogoVersionRequest
   * @param {BrandRequest} brandRequest
   * @param {boolean} isChecked - Whether LogoVersion is selected or not
   * @param {string=} selectedBrandId
   */
  async updateLogoVersionRequest(
    logoVersionBase,
    brandRequest,
    isChecked,
    triggerUpdate = false,
    selectedBrandId
  ) {
    // Check if there's an existing LogoVersionRequest created previously
    const logoVersionRequest = brandRequest.logoVersionRequests.find(
      lvr =>
        logoVersionBase.id === lvr.logoVersionId ||
        logoVersionBase.id === lvr.id
    );

    const requestStatus = isChecked
      ? LOGO_VERSION_REQUEST_STATUSES.CHECKED
      : LOGO_VERSION_REQUEST_STATUSES.DELETED;

    if (logoVersionRequest) {
      DataService.updateLogoVersionRequest(
        logoVersionRequest.id,
        requestStatus
      );
      logoVersionRequest.status = requestStatus;
      // Create new LogoVersionRequest only if we need to set staus to checked
    } else if (!logoVersionRequest && isChecked) {
      const logoVersionRequestId = await DataService.createLogoVersionRequest({
        brandRequestId: brandRequest.id,
        brandId: selectedBrandId,
        logoVersionId: logoVersionBase.id,
        status: requestStatus
      });
      brandRequest.logoVersionRequests.push(
        new LogoVersionRequest({
          id: logoVersionRequestId,
          logoVersionId: logoVersionBase.id,
          status: requestStatus
        })
      );
    }

    // If a LogoVersion is checked and BrandRequest has Ignored status -> set BrandRequest to Confirmed
    if (isChecked && brandRequest.status === BRAND_REQUEST_STATUSES.IGNORED) {
      DataService.updateBrandRequest(brandRequest.id, {
        status: BRAND_REQUEST_STATUSES.CONFIRMED
      });
      brandRequest.status = BRAND_REQUEST_STATUSES.CONFIRMED;
    }

    if (triggerUpdate) {
      this.renderBrandRequests(brandRequest);
    }
  }

  /**
   * Immutable update to brandRequests collection. Causes Table to re-render.
   * @param {BrandRequest} updatedBrandRequest
   */
  renderBrandRequests(updatedBrandRequest) {
    const { brandRequests, brandRequestGroup } = this.state;

    const updatedBrandRequestIndex = brandRequests.findIndex(
      brandRequest => brandRequest.id === updatedBrandRequest.id
    );

    const newUpdatedBrandRequest = Object.assign({}, updatedBrandRequest);
    const newBrandRequests = this.updateObjectInArray(brandRequests, {
      index: updatedBrandRequestIndex,
      item: newUpdatedBrandRequest
    });

    // Set/remove the completion flag
    const toBeProcessedCount = this.getToBeProcessedCount();
    if (
      toBeProcessedCount === 0 &&
      brandRequestGroup.status !== BRAND_REQUEST_GROUP_STATUSES.USER_DONE
    ) {
      brandRequestGroup.status = BRAND_REQUEST_GROUP_STATUSES.USER_DONE;
      DataService.updateBrandRequestGroup(brandRequestGroup.id, {
        status: BRAND_REQUEST_GROUP_STATUSES.USER_DONE
      });
    } else if (
      toBeProcessedCount > 0 &&
      brandRequestGroup.status === BRAND_REQUEST_GROUP_STATUSES.USER_DONE
    ) {
      brandRequestGroup.status =
        BRAND_REQUEST_GROUP_STATUSES.CSV_UPLOAD_COMPLETE;
      DataService.updateBrandRequestGroup(brandRequestGroup.id, {
        status: BRAND_REQUEST_GROUP_STATUSES.CSV_UPLOAD_COMPLETE
      });
    }

    this.setState({
      brandRequests: newBrandRequests
    });
  }

  /**
   *
   * @param {BrandRequest} brandRequest
   * @param {boolean=} isChecked
   */
  async updateBrandRequest(brandRequest, isChecked) {
    const { brandRequests, selectedBrandIndexes } = this.state;
    const brandRequestIndex = brandRequests.findIndex(
      _brandRequest => _brandRequest.id === brandRequest.id
    );
    const selectedBrandIndex = selectedBrandIndexes[brandRequestIndex];

    const selectedBrand = brandRequest.brands[selectedBrandIndex];

    let status = BRAND_REQUEST_STATUSES.IGNORED;

    if (selectedBrand && !selectedBrand.id && isChecked) {
      status = BRAND_REQUEST_STATUSES.NEW;
    } else if (isChecked || isChecked === undefined) {
      status = BRAND_REQUEST_STATUSES.CONFIRMED;
    }

    DataService.updateBrandRequest(brandRequest.id, { status });
    brandRequest.status = status;

    if (selectedBrandIndex !== undefined) {
      // Update or create LogoVersionRequest for each existing LogoVersion
      await Promise.all(
        selectedBrand.logoVersions.map(logoVersion =>
          this.updateLogoVersionRequest(
            logoVersion,
            brandRequest,
            isChecked,
            false,
            selectedBrand.id
          )
        )
      );

      // Update LogoVersions uploaded by user
      brandRequest.logoVersionRequests
        // It should check all the LVR for selected brand.
        // It should uncheck all the LVRs for this BrandRequest
        .filter(
          lvr =>
            !lvr.logoVersionId &&
            (!isChecked || (isChecked && lvr.brandId == selectedBrand.id))
        )
        .forEach(logoVersionRequest => {
          this.updateLogoVersionRequest(
            logoVersionRequest,
            brandRequest,
            isChecked
          );
        });
    }

    this.renderBrandRequests(brandRequest);
  }

  selectNextRow({ isChecked }) {
    const { selectedBrandRequest, brandRequests } = this.state;

    // Update BrandRequest
    this.updateBrandRequest(selectedBrandRequest, isChecked);

    // Select next row
    const selectedBrandRequestIndex = brandRequests.findIndex(
      brandRequest => brandRequest.id === selectedBrandRequest.id
    );

    // Check for the last row
    if (selectedBrandRequestIndex + 1 <= brandRequests.length - 1) {
      this.setState({
        selectedBrandRequest: brandRequests[selectedBrandRequestIndex + 1]
      });
    } else {
      this.setState({
        selectedBrandRequest: new BrandRequest(),
        isSideBarOpen: false
      });
    }
  }

  updateObjectInArray(array, action) {
    return array.map((item, index) => {
      if (index !== action.index) {
        // This isn't the item we care about - keep it as-is
        return item;
      }

      // Otherwise, this is the one we want - return an updated value
      return {
        ...item,
        ...action.item
      };
    });
  }

  onSidebarConfirmClick() {
    this.selectNextRow({ isChecked: true });
  }

  onSidebarIgnoreClick() {
    this.selectNextRow({ isChecked: false });
  }

  onSidebarBrandRequestChange(newProps) {
    const { selectedBrandRequest } = this.state;

    Object.assign(selectedBrandRequest, newProps);

    this.setState({
      selectedBrandRequest
    });

    this.renderBrandRequests(selectedBrandRequest);
  }

  onSidebarBlur() {
    const { selectedBrandRequest } = this.state;
    const {
      contentWebsite,
      industryId,
      name,
      countryId,
      note
    } = selectedBrandRequest;
    DataService.updateBrandRequest(selectedBrandRequest.id, {
      contentWebsite,
      name,
      industryId,
      countryId,
      note
    });
  }

  async onLogoSelected(files) {
    const {
      selectedBrandRequest,
      brandRequests,
      selectedBrandIndexes
    } = this.state;
    const selectedBrandRequestIndex = brandRequests.findIndex(
      brandRequest => brandRequest.id === selectedBrandRequest.id
    );
    const selectedBrandIndex =
      selectedBrandIndexes[selectedBrandRequestIndex] || 0;
    const selectedBrand = selectedBrandRequest.brands[selectedBrandIndex];

    let similarLogoDetected = false;

    await Promise.all(
      files.map(async file => {
        const data = await DataService.uploadLogoVersionRequestImage(
          selectedBrandRequest.id,
          file,
          // Deleted just means unticked in this case
          LOGO_VERSION_REQUEST_STATUSES.DELETED,
          selectedBrand.id
        );
        if (data.similarity && data.similarity.length) {
          similarLogoDetected = true;
        }
        await DataService.getId("logverreq_" + data.id).then(
          ({ logoVersionRequest }) => {
            selectedBrandRequest.logoVersionRequests.push(logoVersionRequest);
          }
        );
      })
    );

    if (similarLogoDetected) {
      // Re-query the information about particular brand request
      const brandRequest = await DataService.getBrandRequest(
        selectedBrandRequest.id
      );
      brandRequests[selectedBrandRequestIndex] = brandRequest;
      // Recalculate index as it has moved with addition of similar logos
      selectedBrandIndexes[
        selectedBrandRequestIndex
      ] = brandRequest.brands.findIndex(brand => brand.id === selectedBrand.id);

      this.setState({
        hasVisualDuplicates: true,
        selectedBrandIndexes
      });
    }

    this.setState({
      selectedBrandRequest
    });
  }

  async onBatchUploadClick(visualClassGroupId, detectionSettingId) {
    const { brandRequestGroupId } = this.props.match.params;
    try {
      await DataService.uploadBatch(
          brandRequestGroupId,
          visualClassGroupId,
          detectionSettingId
      );
      window.location.href = "../";
    }
    catch (e) {
      console.error(e);
      toast.error('Operation Failed!!!!', {
        position: "top-right",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: true,
        draggable: false,
        progress: undefined,
      });
    }


  }

  async onToolbarDeleteBrandRequestGroup() {
    const { brandRequestGroup } = this.state;
    if (
      // eslint-disable-next-line no-restricted-globals
      confirm(
        `Are you sure you want to permanently delete "${brandRequestGroup.name}" created on ${brandRequestGroup.dateEntered}, id: ${brandRequestGroup.id}?`
      )
    ) {
      await DataService.updateBrandRequestGroup(brandRequestGroup.id, {
        status: BRAND_REQUEST_GROUP_STATUSES.DELETED
      });
      window.location.href = "../";
    }
  }

  onToolbarUpdateBrandRequestGroupName(newBrandRequestGroupName) {
    const { brandRequestGroup } = this.state;
    DataService.updateBrandRequestGroup(brandRequestGroup.id, {
      name: newBrandRequestGroupName
    });
    brandRequestGroup.name = newBrandRequestGroupName;

    this.setState({
      brandRequestGroup
    });
  }

  async onBrandSelect(brandId) {
    const { selectedBrandRequest, brandRequests } = this.state;

    const existingBrandIndex = selectedBrandRequest.brands.findIndex(
      brand => brand.id === `bra_${brandId}`
    );
    // Select this brand in the Table and SideBar
    const selectedBrandRequestIndex = brandRequests.findIndex(
      brandRequest => brandRequest.id === selectedBrandRequest.id
    );

    if (existingBrandIndex !== -1) {
      this.onTableSimilarBrandSelect(
        selectedBrandRequestIndex,
        existingBrandIndex
      );
      return;
    }

    const brandRequestBrandId = await DataService.createBrandRequestBrand(
      selectedBrandRequest.id,
      brandId
    );
    const brandRequestBrand = await DataService.getBrandRequestBrand(
      brandRequestBrandId
    );

    // Add selected brand to the Similar Brands column
    selectedBrandRequest.brands.push(new Brand(brandRequestBrand.brand));
    selectedBrandRequest.brandRequestBrands.push(brandRequestBrand);

    this.setState({
      selectedBrandRequest
    });

    // Last brand that was added
    const brandIndex = selectedBrandRequest.brands.length - 1;

    this.onTableSimilarBrandSelect(selectedBrandRequestIndex, brandIndex);
  }

  onTableSimilarBrandSelect(brandRequestIndex, brandIndex) {
    const { brandRequests, selectedBrandIndexes } = this.state;

    const selectedBrandRequest = brandRequests[brandRequestIndex];
    const newBrandRequestBrand =
      selectedBrandRequest.brandRequestBrands[brandIndex - 1];
    const oldBrandRequestBrand =
      selectedBrandRequest.brandRequestBrands[
        selectedBrandIndexes[brandRequestIndex] - 1
      ];

    // Untick all the previously selected logos
    this.updateBrandRequest(selectedBrandRequest);

    // Mark Similar Brand as selected
    if (newBrandRequestBrand === undefined) {
      DataService.updateBrandRequest(selectedBrandRequest.id, {
        status: BRAND_REQUEST_STATUSES.NEW
      });
    } else {
      DataService.updateBrandRequestBrand(
        newBrandRequestBrand.id,
        BRAND_REQUEST_BRAND_STATUSES.SELECTED
      );
    }

    // Unmark previous Similar Brand
    if (oldBrandRequestBrand === undefined) {
      DataService.updateBrandRequest(selectedBrandRequest.id, {
        status: BRAND_REQUEST_STATUSES.CONFIRMED
      });
    } else if (
      oldBrandRequestBrand &&
      oldBrandRequestBrand !== newBrandRequestBrand
    ) {
      DataService.updateBrandRequestBrand(
        oldBrandRequestBrand.id,
        BRAND_REQUEST_BRAND_STATUSES.CREATED
      );
    }

    const newSelectedBrandIndexes = Object.assign({}, selectedBrandIndexes, {
      [brandRequestIndex]: brandIndex
    });
    this.setState({
      selectedBrandIndexes: newSelectedBrandIndexes
    });
  }

  onSidebarAddNewMeta(metaKey, metaValue) {
    const { selectedBrandRequest } = this.state;

    if (!selectedBrandRequest.meta) {
      selectedBrandRequest.meta = {};
    }

    if (selectedBrandRequest.meta[metaKey] === undefined) {
      selectedBrandRequest.meta[metaKey] = metaValue;

      DataService.updateBrandRequest(selectedBrandRequest.id, {
        meta: selectedBrandRequest.meta
      });

      return true;
    } else {
      alert(`Meta key "${metaKey}" already defined!`);
      return false;
    }
  }

  onSidebarDeleteMeta(metaKey) {
    const { selectedBrandRequest } = this.state;

    if (
      // eslint-disable-next-line no-restricted-globals
      confirm(
        `Are you sure you want to delete this meta key: "${metaKey} - ${selectedBrandRequest.meta[metaKey]}"?`
      )
    ) {
      delete selectedBrandRequest.meta[metaKey];

      DataService.updateBrandRequest(selectedBrandRequest.id, {
        meta: selectedBrandRequest.meta
      });
    }
  }

  getToBeProcessedCount() {
    const { brandRequests } = this.state;

    return brandRequests.filter(
      brandRequest =>
        brandRequest.status !== BRAND_REQUEST_STATUSES.IGNORED &&
        brandRequest.logoVersionRequests.every(
          lvr => lvr.status === LOGO_VERSION_REQUEST_STATUSES.DELETED
        )
    ).length;
  }

  render() {
    const {
      isSideBarOpen,
      brandRequests,
      selectedBrandRequest,
      selectedBrandIndexes,
      brandRequestGroup,
      hasVisualDuplicates
    } = this.state;

    const { isAdmin } = this.props;

    const toBeProcessedCount = this.getToBeProcessedCount();

    const isUploadBatchButtonDisabled = toBeProcessedCount > 0;

    let selectedBrandIndex;
    if (isSideBarOpen) {
      const selectedBrandRequestIndex = brandRequests.findIndex(
        brandRequest => brandRequest.id === selectedBrandRequest.id
      );
      selectedBrandIndex = selectedBrandIndexes[selectedBrandRequestIndex];
    }

    const selectedBrand = selectedBrandRequest.brands[selectedBrandIndex];
    const isConfirmDisabled = Utils.isConfirmDisabled(
      selectedBrandRequest,
      selectedBrandIndex
    );
    const geoCountryId = Utils.getGeoCountryId(
      selectedBrand,
      selectedBrandRequest
    );
    const industryId = Utils.getIndustryId(selectedBrand, selectedBrandRequest);

    return (
      <div className={`${containerFluid} ${lgContainerFluid}`}>
        <Toolbar
          brandRequestGroupName={brandRequestGroup.name}
          brandRequestGroupId={brandRequestGroup.id}
          showUploadBatchButton={
            brandRequestGroup.status !== BRAND_REQUEST_GROUP_STATUSES.FINISHED
          }
          toBeProcessedCount={toBeProcessedCount}
          onDeleteBrandRequestGroup={this.onToolbarDeleteBrandRequestGroup}
          onUpdateBrandRequestGroupName={
            this.onToolbarUpdateBrandRequestGroupName
          }
          isUploadBatchButtonDisabled={isUploadBatchButtonDisabled}
          isAdmin={isAdmin}
          hasVisualDuplicates={hasVisualDuplicates}
        />
        <div
          className={`${tableWrapper} ${
            isSideBarOpen ? tableWrapperShrinked : ""
          }`}
        >
          <div className={`${row}`}>
            <div className={`${col}`}>
              <Table
                brandRequests={brandRequests}
                onRowClick={this.onTableRowClick}
                selectedBrandRequestId={selectedBrandRequest.id}
                selectedBrandIndexes={selectedBrandIndexes}
                onLogoVersionCheck={(logoVersionBase, isChecked) => {
                  const selectedBrand =
                    selectedBrandRequest.brands[selectedBrandIndex];

                  this.updateLogoVersionRequest(
                    logoVersionBase,
                    selectedBrandRequest,
                    isChecked,
                    true,
                    selectedBrand.id
                  );

                  if (!selectedBrand.id && isChecked) {
                    // It's a new brand, mark it for creation
                    DataService.updateBrandRequest(selectedBrandRequest.id, {
                      status: BRAND_REQUEST_STATUSES.NEW
                    });
                  }
                }}
                onBrandRequestCheck={(brandRequest, isChecked) => {
                  this.updateBrandRequest(brandRequest, isChecked).catch(
                    reason => {
                      console.log(reason);
                    }
                  );
                }}
                onSimilarBrandClick={this.onTableSimilarBrandSelect}
              />
            </div>
          </div>
        </div>
        <Sidebar
          className={`${sidebar} ${isSideBarOpen ? sidebarOpened : ""}`}
          brandRequest={selectedBrandRequest}
          onConfirmClick={this.onSidebarConfirmClick}
          onIgnoreClick={this.onSidebarIgnoreClick}
          onLogoSelected={this.onLogoSelected}
          selectedBrandIndex={selectedBrandIndex}
          onBrandRequestChange={this.onSidebarBrandRequestChange}
          onBlur={this.onSidebarBlur}
          geoCountryId={geoCountryId}
          industryId={industryId}
          note={selectedBrandRequest.note}
          isConfirmDisabled={isConfirmDisabled}
          onAddNewMeta={this.onSidebarAddNewMeta}
          onDeleteMeta={this.onSidebarDeleteMeta}
        />
        <UploadBatchModal
          onBatchUploadClick={this.onBatchUploadClick}
          detectionSettingId={brandRequestGroup.detectionSettingId}
        />
        <BrandSearchModal onBrandSelect={this.onBrandSelect} />
        <ReferenceImagesModal exampleUrls={selectedBrandRequest.exampleUrls} />
      </div>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js